/**
 * aiGenerationService.js — Frontend service for AI article generation
 *
 * Routes ALL generation requests through the backend Express server.
 * API keys are NEVER handled on the frontend — they live in server env vars.
 *
 * The frontend sends: provider, model, topic, keywords, tone, length,
 *                     article_type, custom_instructions, mcp_config
 *
 * The backend resolves the key, calls the AI provider, and returns the text.
 */

const AI_API_BASE = import.meta.env.VITE_AI_API_URL || 'http://localhost:4000/api/ai';
const TIMEOUT_MS  = 65_000; // Slightly longer than server timeout

/**
 * Fetch with timeout using AbortController.
 */
async function fetchWithTimeout(url, options, timeoutMs = TIMEOUT_MS) {
  const controller = new AbortController();
  const timer      = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Generate an article draft via the backend AI route.
 *
 * @param {{
 *   provider:             string,
 *   model:                string,
 *   topic:                string,
 *   keywords?:            string[],
 *   tone?:                string,
 *   length?:              string,
 *   article_type?:        string,
 *   custom_instructions?: string,
 *   mcp_config?:          object,
 * }} params
 *
 * @returns {Promise<{
 *   text:        string,
 *   model:       string,
 *   provider:    string,
 *   word_count:  number,
 *   usage:       object|null,
 *   mcp_rounds:  number|null,
 *   duration_ms: number,
 * }>}
 */
export async function generateArticle(params) {
  const {
    provider, model, topic, keywords = [], tone = 'compassionate',
    length = 'medium', article_type = 'tribute',
    custom_instructions = '', mcp_config = null,
  } = params;

  if (!provider) throw new Error('provider is required.');
  if (!model)    throw new Error('model is required.');
  if (!topic || topic.trim().length < 3) throw new Error('topic must be at least 3 characters.');

  let res;
  try {
    res = await fetchWithTimeout(
      `${AI_API_BASE}/generate`,
      {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          provider, model, topic, keywords, tone, length,
          article_type, custom_instructions, mcp_config,
        }),
      },
      TIMEOUT_MS
    );
  } catch (err) {
    if (err.name === 'AbortError')
      throw new Error('Request timed out. The AI provider took too long to respond. Try a shorter length.');
    throw new Error(`Network error: ${err.message}`);
  }

  const data = await res.json();

  if (!res.ok) {
    // Surface the backend error message directly
    throw new Error(data?.error || `Server error ${res.status}`);
  }

  if (!data.text || data.text.trim().length === 0)
    throw new Error('The AI returned an empty response. Please try again.');

  return {
    text:        data.text,
    model:       data.model,
    provider:    data.provider,
    word_count:  data.word_count,
    usage:       data.usage    ?? null,
    mcp_rounds:  data.mcp_rounds ?? null,
    duration_ms: data.duration_ms ?? null,
  };
}

/**
 * Fetch provider status from the backend (which keys are configured).
 * Returns a map of provider id → { configured, source, label, models }
 *
 * @returns {Promise<Record<string, object>>}
 */
export async function fetchProviderStatus() {
  try {
    const res  = await fetch(`${AI_API_BASE}/status`);
    const data = await res.json();
    return data.providers || {};
  } catch {
    return {};
  }
}

/**
 * Admin: update an API key on the server (runtime override).
 * Requires X-Admin-Token header.
 *
 * @param {string} provider
 * @param {string} apiKey
 * @param {string} adminToken
 * @returns {Promise<{ success: boolean, message?: string, error?: string }>}
 */
export async function setProviderKey(provider, apiKey, adminToken) {
  try {
    const res  = await fetch(`${AI_API_BASE}/keys`, {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'X-Admin-Token': adminToken,
      },
      body: JSON.stringify({ provider, api_key: apiKey }),
    });
    return await res.json();
  } catch (err) {
    return { success: false, error: err.message };
  }
}

/**
 * Admin: clear a runtime key override (reverts to env variable).
 *
 * @param {string} provider
 * @param {string} adminToken
 * @returns {Promise<{ success: boolean, message?: string, error?: string }>}
 */
export async function clearProviderKey(provider, adminToken) {
  try {
    const res  = await fetch(`${AI_API_BASE}/keys/${provider}`, {
      method:  'DELETE',
      headers: { 'X-Admin-Token': adminToken },
    });
    return await res.json();
  } catch (err) {
    return { success: false, error: err.message };
  }
}